import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export const WISDOM_KIND = 5951;

export interface WisdomLetter {
  event: NostrEvent;
  title: string;
  subject: string;
  topics: string[];
  body: string;
}

function parseWisdomLetter(event: NostrEvent): WisdomLetter | null {
  if (event.kind !== WISDOM_KIND) return null;

  const title = event.tags.find(([n]) => n === 'title')?.[1];
  const subject = event.tags.find(([n]) => n === 'subject')?.[1];
  const topics = event.tags.filter(([n]) => n === 't').map(([, v]) => v);

  if (!title || !subject || !event.content.trim()) return null;

  return {
    event,
    title,
    subject,
    topics,
    body: event.content,
  };
}

interface UseWisdomLettersOptions {
  topic?: string;
  limit?: number;
}

export function useWisdomLetters({ topic, limit = 30 }: UseWisdomLettersOptions = {}) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wisdom-letters', topic ?? 'all', limit],
    queryFn: async (ctx) => {
      const filter = topic
        ? { kinds: [WISDOM_KIND], '#t': [topic], limit }
        : { kinds: [WISDOM_KIND], limit };

      const events = await nostr.query([filter], { signal: ctx.signal });

      return events
        .map(parseWisdomLetter)
        .filter((l): l is WisdomLetter => l !== null)
        .sort((a, b) => b.event.created_at - a.event.created_at);
    },
    staleTime: 60_000,
  });
}

export function useWisdomLetter(eventId: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['wisdom-letter', eventId ?? ''],
    queryFn: async (ctx) => {
      if (!eventId) return null;

      const [event] = await nostr.query(
        [{ kinds: [WISDOM_KIND], ids: [eventId], limit: 1 }],
        { signal: ctx.signal }
      );

      if (!event) return null;
      return parseWisdomLetter(event);
    },
    enabled: Boolean(eventId),
    staleTime: 5 * 60_000,
  });
}
